
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DiaryEntry } from "@/utils/diary/types";
import { getSharedEntry } from "@/utils/supabase";
import EntryCard from "@/components/EntryCard";
import { ArrowLeft, Lock } from "lucide-react";

// Default entry when loading
const defaultEntry: DiaryEntry = {
  id: "",
  userId: "",
  babyName: "",
  entryText: "Loading...",
  createdAt: new Date().toISOString()
};

const SharedEntry = () => {
  const { accessCode } = useParams<{ accessCode: string }>();
  const [entry, setEntry] = useState<DiaryEntry>(defaultEntry);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessCode) {
      setError("Invalid access code");
      setLoading(false);
      return;
    }

    const loadSharedEntry = async () => {
      try {
        const { data, error } = await getSharedEntry(accessCode, window.location.pathname.split('/').pop() || '');
        if (error) throw error;
        
        if (data) {
          // Transform the shared entry data into DiaryEntry format
          const entryData: DiaryEntry = {
            id: data.entry_id,
            userId: data.owner_id,
            babyName: "", // Will be populated when the actual entry is fetched
            entryText: "Loading entry...",
            createdAt: data.created_at
          };
          setEntry(entryData);
        } else {
          setError("Entry not found");
        }
      } catch (err) {
        console.error("Error loading shared entry:", err);
        setError("Failed to load entry");
      } finally {
        setLoading(false);
      }
    };

    loadSharedEntry();
  }, [accessCode]);

  if (loading) {
    return (
      <div className="container mx-auto mt-20 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto mt-20 px-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-2">
              <Lock className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle>Access Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto mt-20 px-4 mb-20">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Home
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Shared Baby Diary Entry</h1>
          <p className="text-muted-foreground mt-1">
            This is a shared baby diary entry
          </p>
        </div>

        <EntryCard entry={entry} onDelete={() => {}} readOnly={true} />
      </div>
    </div>
  );
};

export default SharedEntry;
